/**
 * Centralized translator for Clerk API errors. Returns a user-facing message,
 * optionally masking codes that would leak whether an account exists (OWASP
 * user enumeration). Each auth flow passes its own enumeration-sensitive code
 * set via `maskCodes`.
 *
 * Clerk error shape: ``{ errors: [{ code, message, longMessage, ... }] }``.
 */

interface ClerkErrorShape {
  code?: string
  longMessage?: string
  message?: string
}

export interface ClerkErrorOptions {
  /**
   * Error codes that should collapse into ``maskMessage`` instead of surfacing
   * Clerk's verbatim text. Use for sign-in / password-reset paths where
   * distinguishing "account not found" from "wrong password" leaks info.
   */
  maskCodes?: ReadonlySet<string>
  /** Replacement message for masked codes. Required if ``maskCodes`` is set. */
  maskMessage?: string
  /**
   * Per-code overrides (e.g. friendlier copy for ``form_code_incorrect``).
   * Looked up before the longMessage/message fallback.
   */
  overrides?: Readonly<Record<string, string>>
}

export function describeClerkError(
  err: unknown,
  fallback: string,
  opts: ClerkErrorOptions = {},
): string {
  const first = extractFirstError(err)
  if (!first) return fallback
  if (opts.maskCodes && first.code && opts.maskCodes.has(first.code)) {
    return opts.maskMessage ?? fallback
  }
  if (first.code && opts.overrides && first.code in opts.overrides) {
    return opts.overrides[first.code] ?? fallback
  }
  if (first.longMessage) return first.longMessage
  if (first.message) return first.message
  return fallback
}

function extractFirstError(err: unknown): ClerkErrorShape | null {
  if (
    typeof err !== 'object' ||
    err === null ||
    !('errors' in err) ||
    !Array.isArray(err.errors)
  ) {
    return null
  }
  const errors = err.errors as ClerkErrorShape[]
  return errors[0] ?? null
}

/**
 * Detect whether a Clerk error is one of the enumeration-leaking codes.
 * Use to branch flow (e.g. password reset silently advances to verify
 * step on `form_identifier_not_found` instead of showing an error).
 * Cleaner than coercing ``describeClerkError`` to return an empty-string
 * sentinel and checking it.
 */
export function isEnumerationError(err: unknown, codes: ReadonlySet<string>): boolean {
  const first = extractFirstError(err)
  return Boolean(first?.code && codes.has(first.code))
}

/** Sign-in codes that reveal whether the email is registered. Pair with a
 *  generic "Email ou senha incorretos" message. */
export const SIGNIN_ENUMERATION_CODES: ReadonlySet<string> = new Set([
  'form_identifier_not_found',
  'form_password_incorrect',
])

/** Password-reset codes equivalent to "this email isn't registered". Use to
 *  silently advance to the verification step (caller decides how to mask). */
export const PASSWORD_RESET_ENUMERATION_CODES: ReadonlySet<string> = new Set([
  'form_identifier_not_found',
])
